<template>
  <div class="session-history-view">
    <!-- Header -->
    <div class="header-section">
      <div class="agent-info">
        <!-- <div class="agent-avatar">A</div> -->
        <div>
          <h3 class="agent-name">{{ companyName }}</h3>
          <span class="create-date"> Session History - {{ createdAt }}</span>
        </div>
      </div>

      <div v-if="!isLoading" class="session-search">
        <div class="session-search__input-wrap">
          <i class="bi bi-search session-search__icon"></i>
          <input
            v-model="searchQuery"
            type="text"
            class="session-search__input"
            placeholder="Search conversation…"
            @keydown.enter.prevent="goToNextMatch"
          />
          <button
            v-if="searchQuery"
            class="session-search__clear"
            title="Clear search"
            @click="clearSearch"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
        <div v-if="searchQuery" class="session-search__nav">
          <span class="session-search__count">{{ totalMatches ? `${currentMatchPointer + 1} / ${totalMatches}` : "0 / 0" }}</span>
          <button class="session-search__nav-btn" :disabled="!totalMatches" title="Previous match" @click="goToPrevMatch">
            <i class="bi bi-chevron-up"></i>
          </button>
          <button class="session-search__nav-btn" :disabled="!totalMatches" title="Next match" @click="goToNextMatch">
            <i class="bi bi-chevron-down"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="main-content-session-history">
      <!-- Conversation Section -->
      <div class="conversation-section">
        <div v-if="isLoading" class="messages-container">
          <!-- Message Skeleton Loaders -->
          <div
            v-for="i in 5"
            :key="'msg-skeleton-' + i"
            :class="[
              'message-wrapper',
              'skeleton-wrapper',
              i % 2 === 0 ? 'user' : 'agent',
            ]"
          >
            <div class="skeleton skeleton-avatar"></div>
            <div class="message-bubble">
              <div class="skeleton skeleton-text"></div>
              <div class="skeleton skeleton-time"></div>
            </div>
          </div>
        </div>
        <div v-else class="messages-container">
          <div v-if="messages.length === 0" class="empty-state">
            <p class="empty-state-text">No messages yet</p>
          </div>
          <div
            v-else
            v-for="(message, index) in messages"
            :id="`session-msg-${index}`"
            :key="index"
            :class="['message-wrapper', message.role, { 'is-search-match': isMatch(index) }]"
          >
            <div class="message-avatar">
              {{ message.role === "user" ? "U" : "A" }}
            </div>
            <div class="message-bubble">
              <div
                v-if="message.role === 'agent'"
                class="message-text markdown-content"
                v-html="renderAgentText(message)"
              ></div>
              <div v-else class="message-text" v-html="renderUserText(message)"></div>
              <div class="message-time">{{ message.time }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Wishlist Section -->
      <div class="wishlist-section">
        <div class="wishlist-header">
          <div class="wishlist-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                fill="var(--primary-color)"
              />
            </svg>
          </div>
          <div>
            <h3 class="wishlist-title">My Wishlist</h3>
            <p class="wishlist-subtitle">
              {{ isLoading ? "..." : wishlist.length }} items saved
            </p>
          </div>
        </div>

        <div v-if="isLoading" class="wishlist-items">
          <!-- Wishlist Skeleton Loaders -->
          <div
            v-for="i in 3"
            :key="'wishlist-skeleton-' + i"
            class="wishlist-card"
          >
            <div class="skeleton skeleton-image"></div>
            <div class="wishlist-card-content">
              <div class="skeleton skeleton-title"></div>
              <div class="skeleton skeleton-description"></div>
              <div
                class="skeleton skeleton-description"
                style="width: 80%"
              ></div>
              <div class="wishlist-footer">
                <div class="skeleton skeleton-price"></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="wishlist-items">
          <div v-if="wishlist.length === 0" class="empty-state">
            <p class="empty-state-text">No products yet</p>
          </div>
          <div
            v-else
            v-for="(product, index) in wishlist"
            :key="index"
            class="wishlist-card group"
            @click="toggleTooltip(index)"
          >
            <div class="wishlist-image-wrapper">
              <img
                :src="
                  product.images_urls
                    ? product.images_urls[0]
                    : 'https://via.placeholder.com/150'
                "
                loading="lazy"
                :alt="product.title"
                class="wishlist-image"
              />
              <div class="wishlist-image-overlay"></div>
            </div>
            <div class="wishlist-card-content">
              <h3 class="wishlist-title">{{ product.name }}</h3>
              <!-- <p class="wishlist-description">{{ product.description }}</p> -->
              <div class="wishlist-footer">
                <span
                  class="wishlist-brand-label status-badge status-completed"
                  >{{ product.brand }}</span
                >
                <span v-if="product.cart_amount" class="wishlist-qty"
                  >Qty {{ product.cart_amount }}</span
                >
                <span class="wishlist-price"
                  >{{ currency }} {{ parseFloat(product.price).toFixed(2) }}</span
                >
              </div>
              <div v-if="product.cart_amount > 1" class="wishlist-subtotal">
                Subtotal: {{ currency }}
                {{ (parseFloat(product.price) * product.cart_amount).toFixed(2) }}
              </div>
            </div>
            <!-- Tooltip -->
            <div
              v-if="activeTooltip === index"
              class="wishlist-tooltip"
              @click.stop="closeTooltip"
            >
              <div class="tooltip-content" @click.stop>
                <button class="tooltip-close" @click="closeTooltip">
                  &times;
                </button>
                <h4 class="tooltip-title">{{ product.name }}</h4>
                <div
                  class="tooltip-description"
                  v-html="product.description"
                ></div>
              </div>
            </div>
          </div>
        </div>

        <!-- Total Value -->
        <div class="wishlist-total">
          <span class="total-label">Total Value</span>
          <span v-if="isLoading" class="skeleton skeleton-total"></span>
          <span v-else class="total-value">{{ currency }} {{ totalValue }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeMount } from "vue";
import moment from "moment";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import axios from "axios";
import { marked } from "marked";

// Configure marked for better formatting
marked.setOptions({
  breaks: true, // Convert \n to <br>
  gfm: true, // GitHub Flavored Markdown
});

const toast = useToast();
const route = useRoute();
let id = ref(null);
let isLoading = ref(false);
let sessionData = ref(null);
let createdAt = ref(null);
let companyName = ref(null);
let activeTooltip = ref(null);
let currency = ref(null);
const searchQuery = ref("");
const currentMatchPointer = ref(0);
// Mock data for demonstration
const messages = ref([
  //   {
  //     role: 'agent',
  //     content: "Hello! Welcome to our store. How can I help you find the perfect product today?",
  //     time: '10:30 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "Hi! I'm looking for wireless headphones with good noise cancellation.",
  //     time: '10:31 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "Great choice! I'd recommend checking out the Sony WH-1000XM5 or the Bose QuietComfort Ultra. Both have excellent noise cancellation. I've added some options to your wishlist!",
  //     time: '10:32 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "That sounds perfect! What's the price range?",
  //     time: '10:33 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "The Sony WH-1000XM5 is $349.99 and the Bose QuietComfort Ultra is $429.99. Both are premium options with industry-leading noise cancellation. Would you like me to add any specific features you're looking for?",
  //     time: '10:34 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "That sounds perfect! What's the price range?",
  //     time: '10:33 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "The Sony WH-1000XM5 is $349.99 and the Bose QuietComfort Ultra is $429.99. Both are premium options with industry-leading noise cancellation. Would you like me to add any specific features you're looking for?",
  //     time: '10:34 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "That sounds perfect! What's the price range?",
  //     time: '10:33 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "The Sony WH-1000XM5 is $349.99 and the Bose QuietComfort Ultra is $429.99. Both are premium options with industry-leading noise cancellation. Would you like me to add any specific features you're looking for?",
  //     time: '10:34 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "That sounds perfect! What's the price range?",
  //     time: '10:33 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "The Sony WH-1000XM5 is $349.99 and the Bose QuietComfort Ultra is $429.99. Both are premium options with industry-leading noise cancellation. Would you like me to add any specific features you're looking for?",
  //     time: '10:34 AM'
  //   },
  //   {
  //     role: 'user',
  //     content: "That sounds perfect! What's the price range?",
  //     time: '10:33 AM'
  //   },
  //   {
  //     role: 'agent',
  //     content: "The Sony WH-1000XM5 is $349.99 and the Bose QuietComfort Ultra is $429.99. Both are premium options with industry-leading noise cancellation. Would you like me to add any specific features you're looking for?",
  //     time: '10:34 AM'
  //   }
]);

const wishlist = ref([
  //   {
  //     title: 'Sony WH-1000XM5',
  //     description: 'Industry-leading noise cancellation with exceptional sound quality and 30-hour battery life.',
  //     price: '349.99',
  //     image: 'https://images.unsplash.com/photo-1618366712010-f4ae9c647dcb?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Bose QuietComfort Ultra',
  //     description: 'Premium wireless headphones with spatial audio and world-class noise cancellation.',
  //     price: '429.99',
  //     image: 'https://images.unsplash.com/photo-1546435770-a3e426bf472b?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Sennheiser Momentum 4',
  //     description: 'Audiophile-grade sound with adaptive noise cancellation and 60-hour battery.',
  //     price: '379.95',
  //     image: 'https://images.unsplash.com/photo-1484704849700-f032a568e944?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Sony WF-1000XM5',
  //     description: 'Premium true wireless earbuds with industry-leading noise cancellation.',
  //     price: '299.99',
  //     image: 'https://images.unsplash.com/photo-1590658268037-6bf12165a8df?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Beats Studio Pro',
  //     description: 'Personalized spatial audio with USB-C lossless listening and 40-hour battery.',
  //     price: '349.95',
  //     image: 'https://images.unsplash.com/photo-1577174881658-0f30157f1253?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Jabra Elite 85h',
  //     description: 'SmartSound technology with powerful battery and superior call quality.',
  //     price: '249.99',
  //     image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'AKG N700NC M2',
  //     description: 'Studio-quality sound with adaptive noise cancellation and premium comfort.',
  //     price: '279.99',
  //     image: 'https://images.unsplash.com/photo-1545127398-14699f92334b?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Bang & Olufsen Beoplay H95',
  //     description: 'Luxury wireless headphones with titanium drivers and premium materials.',
  //     price: '799.00',
  //     image: 'https://images.unsplash.com/photo-1599669454699-248893623440?w=500&h=500&fit=crop'
  //   },
  //   {
  //     title: 'Audio-Technica ATH-M50xBT2',
  //     description: 'Professional monitor headphones with wireless freedom and exceptional clarity.',
  //     price: '199.00',
  //     image: 'https://images.unsplash.com/photo-1558756520-22cfe5d382ca?w=500&h=500&fit=crop'
  //   }
]);

const totalValue = computed(() => {
  const total = wishlist.value.reduce((sum, product) => {
    const qty = Number(product.cart_amount) || 1;
    return sum + parseFloat(product.price) * qty;
  }, 0);
  return total.toFixed(2);
});

const toggleTooltip = (index) => {
  activeTooltip.value = activeTooltip.value === index ? null : index;
};

const closeTooltip = () => {
  activeTooltip.value = null;
};

// Helper function to process message content
const processMessageContent = (content) => {
  if (!content) return "";

  // Replace \n\n with actual double line breaks for markdown
  // Replace \n with single line breaks
  const processedContent = content
    .replace(/\\n\\n/g, "\n\n")
    .replace(/\\n/g, "\n")
    .replace(/\*\*/g, "**")
    .replace(/\*/g, "*");

  // Convert to markdown HTML
  return marked(processedContent);
};

// ── in-conversation search ────────────────────────────────────────
const matchedMessageIndices = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  if (!q) return [];
  return messages.value.reduce((acc, message, index) => {
    if ((message.text || "").toLowerCase().includes(q)) acc.push(index);
    return acc;
  }, []);
});

const totalMatches = computed(() => matchedMessageIndices.value.length);

function isMatch(index) {
  return matchedMessageIndices.value.includes(index);
}

function escapeHtml(str) {
  const div = document.createElement("div");
  div.textContent = str;
  return div.innerHTML;
}

// Wraps every occurrence of `query` in <mark>, walking only text nodes so
// markup from markdown rendering is never touched.
function highlightHtml(html, query) {
  if (!query) return html;
  const container = document.createElement("div");
  container.innerHTML = html;
  const q = query.toLowerCase();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  let node;
  while ((node = walker.nextNode())) textNodes.push(node);

  textNodes.forEach((textNode) => {
    const text = textNode.textContent;
    const lower = text.toLowerCase();
    if (!lower.includes(q)) return;
    const frag = document.createDocumentFragment();
    let start = 0;
    let idx;
    while ((idx = lower.indexOf(q, start)) !== -1) {
      if (idx > start) frag.appendChild(document.createTextNode(text.slice(start, idx)));
      const mark = document.createElement("mark");
      mark.className = "session-search-hit";
      mark.textContent = text.slice(idx, idx + query.length);
      frag.appendChild(mark);
      start = idx + query.length;
    }
    if (start < text.length) frag.appendChild(document.createTextNode(text.slice(start)));
    textNode.replaceWith(frag);
  });

  return container.innerHTML;
}

function renderAgentText(message) {
  const html = message.formattedText || message.text || "";
  return searchQuery.value ? highlightHtml(html, searchQuery.value) : html;
}

function renderUserText(message) {
  const text = message.text || "";
  return searchQuery.value ? highlightHtml(escapeHtml(text), searchQuery.value) : escapeHtml(text);
}

function scrollToMatch(pointer) {
  const msgIndex = matchedMessageIndices.value[pointer];
  if (msgIndex === undefined) return;
  document
    .getElementById(`session-msg-${msgIndex}`)
    ?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function goToNextMatch() {
  if (!totalMatches.value) return;
  currentMatchPointer.value = (currentMatchPointer.value + 1) % totalMatches.value;
  scrollToMatch(currentMatchPointer.value);
}

function goToPrevMatch() {
  if (!totalMatches.value) return;
  currentMatchPointer.value =
    (currentMatchPointer.value - 1 + totalMatches.value) % totalMatches.value;
  scrollToMatch(currentMatchPointer.value);
}

function clearSearch() {
  searchQuery.value = "";
}

watch(searchQuery, () => {
  currentMatchPointer.value = 0;
  if (totalMatches.value) scrollToMatch(0);
});

const fetchSessionData = () => {
  isLoading.value = true;
  axios
    .get(`${process.env.VUE_APP_BASE_URL}/api/session-history/${id.value}`, {
      headers: {
        Accept: "application/json",
        "Content-type": "application/json",
      },
    })
    .then((res) => {
      // Parse real data when available
      sessionData.value = res.data.data;
      companyName.value = sessionData.value.company?.name;
      createdAt.value = moment(sessionData.value.created_at).format(
        "DD/MM/YYYY - hh:mm A"
      );
      wishlist.value = sessionData.value.wishList;
      currency.value = sessionData.value.wishList.length > 0 ? sessionData.value.wishList[0].currency : "";
      messages.value = sessionData.value.conversation;
      messages.value.forEach((message) => {
        message.time = moment(message.time).format("DD/MM/YYYY - hh:mm A");
        // Format markdown for agent messages
        if (message.role === "agent") {
          message.formattedText = processMessageContent(message.text);
        }
      });
      isLoading.value = false;
    })
    .catch((e) => {
      toast.error(e.response.data.message);
      isLoading.value = false;
      // Keep using mock data on error
    });
};

onBeforeMount(() => {
  id.value = route.params.id;
  fetchSessionData(); // Uncomment when API is ready
});
</script>

<style scoped>
.session-history-view {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--neutral-0);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen,
    Ubuntu, Cantarell, sans-serif;
}

/* Header Section */
.header-section {
  background: var(--neutral-0);
  border-bottom: 1px solid var(--neutral-200);
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}

.agent-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.session-search {
  display: flex;
  align-items: center;
  gap: 8px;
}

.session-search__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.session-search__icon {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--neutral-400);
  pointer-events: none;
}

.session-search__input {
  width: 220px;
  height: 34px;
  padding: 0 30px 0 30px;
  border: 1px solid var(--neutral-200);
  border-radius: 8px;
  font-size: 13px;
  color: var(--neutral-800);
  background: var(--neutral-50);
  outline: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.session-search__input:focus {
  border-color: var(--primary-color);
  background: var(--neutral-0);
}

.session-search__clear {
  position: absolute;
  right: 6px;
  width: 22px;
  height: 22px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--neutral-400);
  border-radius: 50%;
  cursor: pointer;
  font-size: 11px;
}

.session-search__clear:hover {
  background: var(--neutral-200);
  color: var(--neutral-700);
}

.session-search__nav {
  display: flex;
  align-items: center;
  gap: 4px;
}

.session-search__count {
  font-size: 12px;
  color: var(--neutral-500);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  margin-right: 2px;
}

.session-search__nav-btn {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--neutral-200);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-600);
  cursor: pointer;
  font-size: 11px;
}

.session-search__nav-btn:hover:not(:disabled) {
  background: var(--neutral-100);
  color: var(--primary-color);
}

.session-search__nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.message-wrapper.is-search-match .message-text {
  box-shadow: 0 0 0 2px var(--primary-200);
}

:deep(.session-search-hit) {
  background: var(--primary-100);
  color: var(--primary-700);
  border-radius: 3px;
  padding: 0 1px;
}

.agent-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--neutral-100);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 18px;
  color: var(--neutral-700);
}

.agent-name {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--neutral-900);
}

.create-date {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--primary-color);
}

/* Main Content */
.main-content-session-history {
  display: flex;
  flex: 1;
  overflow: hidden;
}

/* Conversation Section */
.conversation-section {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--neutral-200);
}

.messages-container {
  flex: 1;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.message-wrapper {
  display: flex;
  gap: 12px;
  max-width: 70%;
}

.message-wrapper.skeleton-wrapper {
  max-width: 100%;
}

.message-wrapper.user {
  align-self: flex-end;
  flex-direction: row-reverse;
}

.message-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  flex-shrink: 0;
  color: #ffffff;
}

.message-wrapper.agent .message-avatar {
  background: var(--neutral-500);
}

.message-wrapper.user .message-avatar {
  background: var(--primary-color);
}

.message-bubble {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.message-text {
  padding: 12px 16px;
  border-radius: 16px;
  line-height: 1.5;
  font-size: 14px;
}

.message-wrapper.agent .message-text {
  background: var(--neutral-100);
  color: var(--neutral-900);
  border-bottom-left-radius: 4px;
}

.message-wrapper.user .message-text {
  background: var(--primary-color);
  color: #ffffff;
  border-bottom-right-radius: 4px;
}

.message-time {
  font-size: 11px;
  color: var(--neutral-400);
  padding: 0 4px;
}

.message-wrapper.user .message-time {
  text-align: right;
}

/* Markdown styles for agent messages */
.markdown-content :deep(h1),
.markdown-content :deep(h2),
.markdown-content :deep(h3),
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  margin: 8px 0 4px 0;
}

.markdown-content :deep(h1) {
  font-size: 1.5em;
}
.markdown-content :deep(h2) {
  font-size: 1.3em;
}
.markdown-content :deep(h3) {
  font-size: 1.1em;
}
.markdown-content :deep(h4),
.markdown-content :deep(h5),
.markdown-content :deep(h6) {
  font-size: 1em;
}

.markdown-content :deep(p) {
  margin: 0 0 8px 0;
}

.markdown-content :deep(ul),
.markdown-content :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.markdown-content :deep(li) {
  margin: 4px 0;
}

.markdown-content :deep(blockquote) {
  margin: 8px 0;
  padding-left: 12px;
  border-left: 3px solid var(--primary-color);
  color: var(--neutral-500);
}

.markdown-content :deep(code) {
  background: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
  font-size: 13px;
}

.markdown-content :deep(pre) {
  background: rgba(0, 0, 0, 0.05);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.markdown-content :deep(pre code) {
  background: none;
  padding: 0;
}

.markdown-content :deep(strong),
.markdown-content :deep(b) {
  font-weight: 600;
}

.markdown-content :deep(em),
.markdown-content :deep(i) {
  font-style: italic;
}

.markdown-content :deep(a) {
  text-decoration: underline;
}

.markdown-content :deep(hr) {
  border: none;
  border-top: 1px solid var(--neutral-200);
  margin: 12px 0;
}

.markdown-content :deep(table) {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}

.markdown-content :deep(th),
.markdown-content :deep(td) {
  border: 1px solid var(--neutral-200);
  padding: 8px;
  text-align: left;
}

.markdown-content :deep(th) {
  background: var(--neutral-50);
  font-weight: 600;
}

/* Message Input */
.message-input-container {
  padding: 16px 24px;
  border-top: 1px solid var(--neutral-200);
  display: flex;
  gap: 12px;
  background: var(--neutral-0);
}

.message-input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid var(--neutral-200);
  border-radius: 24px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.message-input:focus {
  border-color: var(--primary-color);
}

.send-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: var(--primary-color);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.send-button:hover {
  background: var(--primary-700);
}

/* Wishlist Section */
.wishlist-section {
  width: 400px;
  min-height: 0;
  background: var(--primary-50);
  display: flex;
  flex-direction: column;
}

.wishlist-header {
  padding: 24px;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: var(--primary-100);
  flex-shrink: 0;
}

.wishlist-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: var(--neutral-0);
  display: flex;
  align-items: center;
  justify-content: center;
}

.wishlist-title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--neutral-900);
}

.wishlist-subtitle {
  margin: 4px 0 0;
  font-size: 14px;
  color: var(--neutral-500);
}

.wishlist-items {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

/* Wishlist Card - Converted from React */
.wishlist-card {
  background: var(--neutral-0);
  border-radius: 12px;
  overflow: visible;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(229, 231, 235, 0.5);
  transition: all 0.3s ease;
  animation: scaleIn 0.3s ease-out;
  flex-shrink: 0;
  position: relative;
  cursor: pointer;
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.wishlist-card:hover {
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}

.wishlist-image-wrapper {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 128px;
}

.wishlist-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.wishlist-card:hover .wishlist-image {
  transform: scale(1.1);
}

.wishlist-image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(255, 255, 255, 0.8), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.wishlist-card:hover .wishlist-image-overlay {
  opacity: 1;
}

.wishlist-heart-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(4px);
  border: none;
  cursor: pointer;
  color: #ef4444;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.wishlist-heart-btn:hover {
  background: #ef4444;
  color: white;
}

.wishlist-card-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.wishlist-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--neutral-900);
  display: -webkit-box;
  -webkit-line-clamp: 1;
  line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wishlist-description {
  margin: 0;
  font-size: 14px;
  color: var(--neutral-500);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.wishlist-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-top: 8px;
}

.wishlist-price {
  font-size: 18px;
  font-weight: 700;
  color: var(--primary-color);
}

.wishlist-qty {
  font-size: 12px;
  font-weight: 600;
  color: var(--neutral-500);
  background: var(--neutral-100);
  padding: 2px 8px;
  border-radius: 999px;
  white-space: nowrap;
}

.wishlist-subtotal {
  font-size: 12px;
  font-weight: 500;
  color: var(--neutral-400);
  padding-top: 4px;
}

.wishlist-add-btn {
  padding: 6px 12px;
  background: var(--neutral-100);
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  color: var(--neutral-700);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
}

.wishlist-add-btn:hover {
  background: var(--neutral-200);
}

/* Wishlist Total */
.wishlist-total {
  padding: 20px 24px;
  background: var(--neutral-0);
  border-top: 1px solid var(--neutral-200);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.total-label {
  font-size: 14px;
  color: var(--neutral-500);
  font-weight: 500;
}

.total-value {
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-color);
}

/* Scrollbar Styles */
.conversation-section::-webkit-scrollbar,
.wishlist-items::-webkit-scrollbar {
  width: 6px;
}

.conversation-section::-webkit-scrollbar-track,
.wishlist-items::-webkit-scrollbar-track {
  background: transparent;
}

.conversation-section::-webkit-scrollbar-thumb,
.wishlist-items::-webkit-scrollbar-thumb {
  background: var(--neutral-300);
  border-radius: 3px;
}

.conversation-section::-webkit-scrollbar-thumb:hover,
.wishlist-items::-webkit-scrollbar-thumb:hover {
  background: var(--neutral-400);
}

/* Skeleton Loader Styles */
.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-text {
  height: 80px;
  width: 100%;
  border-radius: 16px;
  margin-bottom: 4px;
}

.message-wrapper.agent .skeleton-text {
  background: var(--neutral-100);
  border-bottom-left-radius: 4px;
}

.message-wrapper.user .skeleton-text {
  background: linear-gradient(90deg, var(--primary-color) 25%, var(--primary-700) 50%, var(--primary-color) 75%);
  background-size: 200% 100%;
  border-bottom-right-radius: 4px;
}

.skeleton-time {
  height: 12px;
  width: 50px;
  margin-left: 4px;
}

.skeleton-image {
  width: 100%;
  height: 128px;
  border-radius: 0;
}

.skeleton-title {
  height: 20px;
  width: 100%;
  margin-bottom: 8px;
}

.skeleton-description {
  height: 16px;
  width: 100%;
  margin-bottom: 6px;
}

.skeleton-price {
  height: 24px;
  width: 80px;
}

.skeleton-total {
  height: 28px;
  width: 100px;
  display: inline-block;
}

/* Empty State */
.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
}

.empty-state-text {
  font-size: 16px;
  color: var(--neutral-400);
  font-weight: 500;
  margin: 0;
}

/* Tooltip Styles */
.wishlist-tooltip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.tooltip-content {
  background: var(--neutral-0);
  border-radius: 12px;
  padding: 24px;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  position: relative;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.tooltip-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  border-radius: 100%;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  color: var(--neutral-500);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.tooltip-close:hover {
  background: none;
  color: var(--neutral-900);
}

.tooltip-title {
  margin: 0 0 16px 0;
  font-size: 20px;
  font-weight: 600;
  color: var(--neutral-900);
  padding-right: 32px;
}

.tooltip-description {
  font-size: 14px;
  line-height: 1.6;
  color: var(--neutral-700);
}

.tooltip-description :deep(p) {
  margin: 0 0 12px 0;
}

.tooltip-description :deep(p:last-child) {
  margin-bottom: 0;
}

.tooltip-description :deep(strong) {
  font-weight: 600;
  color: var(--neutral-900);
}

.tooltip-description :deep(em) {
  font-style: italic;
}

.tooltip-description :deep(ul),
.tooltip-description :deep(ol) {
  margin: 8px 0;
  padding-left: 24px;
}

.tooltip-description :deep(li) {
  margin: 4px 0;
}

/* Responsive */
@media (max-width: 1024px) {
  .wishlist-section {
    width: 350px;
  }
}

@media (max-width: 768px) {
  .main-content-session-history {
    flex-direction: column;
  }

  .wishlist-section {
    width: 100%;
    max-height: 50vh;
  }

  .message-wrapper {
    max-width: 85%;
  }
}

/* ── Dark mode ─────────────────────────────────────────────────
   Surfaces (var(--neutral-0)), text (--text-*) and borders
   (--neutral-*) auto-theme via tokens. Only the brand-tint panels
   and the grey skeleton shimmer need explicit dark overrides. */
[data-theme="dark"] {
  .skeleton {
    background: linear-gradient(90deg, #1e2a3e 25%, #28364e 50%, #1e2a3e 75%);
    background-size: 200% 100%;
  }
  .wishlist-section { background: rgba(78, 127, 255, 0.06); }
  .wishlist-header { background: rgba(78, 127, 255, 0.12); }
}
</style>
